/**
 * Central Invite-Options REST Client
 *
 * Fetches rich invite role options from Central so the Members tab can offer
 * template-aware roles (core role + app-specific persona) instead of a local
 * hierarchy-derived list.
 *
 * Follows the Central convention of scoping membership routes under the
 * workspace path. If Central hasn't shipped this endpoint for the current
 * deployment, we surface that as a distinct error so the UI can decide
 * whether to fall back.
 */

import type { InviteRoleOption } from '@levelup/central-auth';
import { getAccessToken } from './centralAuth';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

export class InviteOptionsError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'InviteOptionsError';
  }
}

/**
 * GET /api/workspaces/:workspaceId/invite-options — returns the rich
 * invite role options that include persona/template-derived member variants.
 *
 * Throws InviteOptionsError with status=404 if the endpoint isn't published
 * on this Central deployment; callers may choose to fall back to the legacy
 * core-roles list.
 */
export async function fetchInviteOptions(
  workspaceId: string,
): Promise<InviteRoleOption[]> {
  const token = getAccessToken();
  if (!token) throw new InviteOptionsError(401, 'Not authenticated');

  const base = getCentralBase();
  if (!base) throw new InviteOptionsError(0, 'VITE_CENTRAL_URL not configured');

  const path = `/api/workspaces/${encodeURIComponent(workspaceId)}/invite-options`;
  const url = new URL(path, base).toString();

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new InviteOptionsError(
      response.status,
      `Central returned ${response.status}`,
    );
  }

  const data = await response.json();
  return (data?.options ?? data?.inviteRoleOptions ?? data ?? []) as InviteRoleOption[];
}
